// Used by: Quote, QuoteDashboard, PortalQuoteOptions
import { useContext, useEffect, useRef, useState } from "react";
import getClassName from "../../utilities/getClassName";
import { getDate, getTime } from "../../utilities/date";
import JourneyLeg from "../shared/JourneyLeg";
import QuoteHeaderJourneyLeg from "./QuoteHeaderJourneyLeg";
import ThemedIcon from "../ui/ThemedIcon";
import QuoteHeaderJourneyLegCard from "./QuoteHeaderJourneyLegCard";
import Background from "../layout/Background";
import planeIcon from "../../assets/icons/plane-light.svg";
import planeIconDark from "../../assets/icons/plane-dark.svg";
import returnTripIcon from "../../assets/icons/return-trip-light.svg";
import returnTripIconDark from "../../assets/icons/return-trip-dark.svg";
// import editIcon from '../../assets/icons/edit-light.svg'
// import editIconDark from '../../assets/icons/edit-dark.svg'
import chevronIcon from "../../assets/icons/chevron-down-white.svg";
import chevronIconDark from "../../assets/icons/chevron-down-dark.svg";
import poweredByContext from "../../store/PoweredByContext";

import { getScheduleDisclaimer } from "../../utilities/scheduleDisclaimer";
import { getTripTypeLabelFromLegCount } from "../../utilities/tripTypeDisplay";

const QuoteHeader = ({ data, filterScrollPosition, isSimple, backgroundColor, ownerApproval }) => {
    const [stickyPosition, setStickyPosition] = useState("");
    const [maximisedHeaderOpacity, setMaximisedHeaderOpacity] = useState("");
    const [minimisedHeaderOpacity, setMinimisedHeaderOpacity] = useState("");
    const [showLittleBackground, setShowLittleBackground] = useState(false);
    const [layout, setLayout] = useState(isSimple ? 12 : 11);
    const [isItineraryExpanded, setIsItineraryExpanded] = useState(false);

    const poweredByCtx = useContext(poweredByContext);

    const stickyHeader = useRef(null);
    const maximisedRef = useRef(null);

    const headerHeight = 55;
    const fixedHeight = 115;
    const fixedPosition = 171;

    useEffect(() => {
        filterScrollPosition(headerHeight + stickyHeader.current.clientHeight - fixedPosition);

        setTimeout(() => {
            resizeHandler();
        }, 1000);

        let resizeTimeout;
        const resizeHandler = () => {
            if (stickyHeader.current) {
                setStickyPosition(-stickyHeader.current.clientHeight + fixedPosition);

                clearTimeout(resizeTimeout);
                resizeTimeout = setTimeout(() => {
                    if (isSimple) {
                        setLayout(12);
                    } else {
                        setLayout(11);
                    }
                }, 100);
            }
        };

        resizeHandler();
        window.addEventListener("resize", resizeHandler);

        const scrollListener = () => {
            if (stickyHeader.current) {
                const headerPosition = stickyHeader.current.getBoundingClientRect().top - headerHeight;
                const endPosition = stickyPosition - headerHeight;
                const percent = (headerPosition / endPosition) * 100;
                setMinimisedHeaderOpacity(((percent - 50) * 2) / 100);
                setMaximisedHeaderOpacity((100 - percent * 1.2) / 100);

                let messageHeight = 0;
                if (document.getElementById("header-message")) {
                    messageHeight = document.getElementById("header-message").clientHeight;
                }

                if (window.scrollY > headerHeight + messageHeight + stickyHeader.current.clientHeight - fixedHeight) {
                    setShowLittleBackground(true);
                } else {
                    setShowLittleBackground(false);
                }
            }
        };

        window.addEventListener("resize", resizeHandler);
        window.addEventListener("scroll", scrollListener);

        return () => {
            window.removeEventListener("resize", resizeHandler);
            window.removeEventListener("scroll", scrollListener);
        };
    }, [filterScrollPosition, stickyPosition, isSimple, layout]);

    useEffect(() => {
        const updateFilterPosition = () => {
            if (stickyHeader.current) {
                filterScrollPosition(headerHeight + stickyHeader.current.clientHeight - fixedPosition);
            }
        };

        const timeoutId = setTimeout(updateFilterPosition, 50);

        return () => clearTimeout(timeoutId);
    }, [isItineraryExpanded, filterScrollPosition]);

    const getTripTypeLabel = () => getTripTypeLabelFromLegCount(data.legs?.length);

    const mapLegForHeader = (leg) => ({
        fromLong: leg.departureAirport.longName.toLowerCase(),
        fromShort: leg.departureAirport.shortName,
        toLong: leg.arrivalAirport.longName.toLowerCase(),
        toShort: leg.arrivalAirport.shortName,
        departureDate: getDate(leg.departureTime),
        departureTime: getTime(leg.departureTime),
        arrivalDate: getDate(leg.arrivalTime),
        arrivalTime: getTime(leg.arrivalTime),
        luggage: leg.luggage,
        passengers: leg.passengers,
        departureTimeZone: leg.departureTimeZone || "",
        arrivalTimeZone: leg.arrivalTimeZone || "",
    });

    const mappedItineraryLegs = data.legs.map(mapLegForHeader);
    const collapseItineraryAfterIndex = data.legs.length > 3 && !isItineraryExpanded ? 3 : null;

    const itineraryChevronIcon = isSimple || poweredByCtx.isPoweredBy ? chevronIconDark : chevronIcon;

    const renderItinerary = () => {
        if (layout === 11) {
            return (
                <QuoteHeaderJourneyLeg
                    legs={mappedItineraryLegs}
                    isSimple={isSimple}
                    collapseAfterIndex={collapseItineraryAfterIndex}
                />
            );
        }

        if (layout === 12) {
            return <QuoteHeaderJourneyLegCard legs={mappedItineraryLegs} isSimple={isSimple} />;
        }

        return data.legs.map((leg, i) => {
            if (collapseItineraryAfterIndex != null && i >= collapseItineraryAfterIndex) {
                return null;
            }

            return (
                <JourneyLeg
                    key={i}
                    journeyType="jets"
                    layout={layout}
                    toLong={leg.arrivalAirport.longName.toLowerCase()}
                    toShort={leg.arrivalAirport.shortName}
                    fromLong={leg.departureAirport.longName.toLowerCase()}
                    fromShort={leg.departureAirport.shortName}
                    departureDate={getDate(leg.departureTime)}
                    departureTime={getTime(leg.departureTime)}
                    arrivalDate={getDate(leg.arrivalTime)}
                    arrivalTime={getTime(leg.arrivalTime)}
                    luggage={leg.luggage}
                    passengers={leg.passengers}
                    isSimple={isSimple}
                    hideLuggageAndPassengers={(layout === 6 && i > 0) || (layout === 4 && i < data.legs.length - 1)}
                    subtitle={data.legs.length > 1 ? (i === 0 ? "Outbound" : "Inbound") : null}
                />
            );
        });
    };

    const backgroundClass =
        backgroundColor === "primary"
            ? "quote-header--bg-primary"
            : backgroundColor === "transparent"
              ? "quote-header--bg-transparent"
              : null;

    return (
        <div
            ref={stickyHeader}
            style={!isSimple ? { top: `${stickyPosition}px` } : null}
            className={getClassName(["quote-header", isSimple ? "quote-header--is-simple" : null, backgroundClass])}
        >
            {isSimple ? (
                <div className="quote-header__tail-number-row quote-header__tail-number-row--is-simple wrap">
                    <div className="quote-header__tail-number">
                        {/* Members area: plain image icon (dark variant), same approach as the popup table */}
                        <img
                            src={data.legs.length > 1 ? returnTripIconDark : planeIconDark}
                            alt=""
                            className="quote-header__tail-number-img"
                        />
                        {data.tripName}
                    </div>
                    {getTripTypeLabel() && <p className="quote-header__trip-type">{getTripTypeLabel()}</p>}
                </div>
            ) : (
                <div
                    style={{
                        height: `${fixedHeight}px`,
                        display: showLittleBackground ? "block" : "none",
                    }}
                    className="quote-header__background-image"
                >
                    <Background position="absolute" />
                </div>
            )}

            <div style={{ opacity: maximisedHeaderOpacity }} className="quote-header__maximised" ref={maximisedRef}>
                <div className="wrap">
                    <div className="quote-header__tail-number-row">
                        <div className="quote-header__tail-number">
                            <ThemedIcon
                                src={data.legs.length > 1 ? returnTripIcon : planeIcon}
                                className="quote-header__tail-number-icon"
                            />
                            {data.tripName}
                        </div>
                        {getTripTypeLabel() && <p className="quote-header__trip-type">{getTripTypeLabel()}</p>}
                    </div>

                    <div className="quote-header__journey-row">{data?.legs && renderItinerary()}</div>

                    <div
                        className={getClassName([
                            "quote-header__itinerary-footer",
                            data.legs.length > 3 && "quote-header__itinerary-footer--multi-leg",
                        ])}
                    >
                        {data.legs.length > 3 && (
                            <button
                                type="button"
                                className={getClassName([
                                    "quote-header__itinerary-button",
                                    isItineraryExpanded && "quote-header__itinerary-button--collapse",
                                ])}
                                onClick={() => setIsItineraryExpanded(!isItineraryExpanded)}
                            >
                                <img src={itineraryChevronIcon} alt="" />
                                {isItineraryExpanded ? "Collapse itinerary" : "See full itinerary"}
                            </button>
                        )}

                        <div
                            className={getClassName([
                                "quote-header__small-print",
                                data.legs.length > 3 && "quote-header__small-print--reserve-space",
                                data.legs.length > 3 && !isItineraryExpanded && "quote-header__small-print--empty",
                            ])}
                        >
                            {(data.legs.length <= 3 || isItineraryExpanded) && (
                                <p>{getScheduleDisclaimer(ownerApproval)}</p>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div style={{ opacity: minimisedHeaderOpacity }} className="quote-header__minified">
                <div className="wrap">
                    {data && (
                        <>
                            {data.legs && (
                                <>
                                    <JourneyLeg
                                        journeyType="jets"
                                        layout={5}
                                        toLong={data.legs[0].arrivalAirport.longName}
                                        toShort={data.legs[0].arrivalAirport.shortName}
                                        fromLong={data.legs[0].departureAirport.longName}
                                        fromShort={data.legs[0].departureAirport.shortName}
                                        departureDate={getDate(data.legs[0].departureTime)}
                                        departureTime={getTime(data.legs[0].departureTime)}
                                        arrivalDate={getDate(data.legs[0].arrivalTime)}
                                        arrivalTime={getTime(data.legs[0].arrivalTime)}
                                        luggage={data.legs[0].luggage}
                                        passengers={data.legs[0].passengers}
                                        isSimple={isSimple}
                                        isReturnTrip={data.legs.length > 1}
                                    />
                                </>
                            )}
                            {isSimple && data.legs.length > 3 && (
                                <button
                                    type="button"
                                    className={getClassName([
                                        "quote-header__itinerary-button",
                                        isItineraryExpanded && "quote-header__itinerary-button--collapse",
                                    ])}
                                    onClick={() => setIsItineraryExpanded(!isItineraryExpanded)}
                                >
                                    <img src={itineraryChevronIcon} alt="" />
                                    {isItineraryExpanded ? "Collapse itinerary" : "See full itinerary"}
                                </button>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default QuoteHeader;
